import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Star, SlidersHorizontal } from "lucide-react";
import TeamLogoImage from "../components/league/TeamLogoImage";
import { useLeague } from "../context/LeagueContext";
import { useAuth } from "../context/AuthContext";
import { useDraftContext } from "../context/DraftContext";
import { usePreseasonStats } from "../hooks/usePreseasonStats";
import { useBigBoard } from "../hooks/useBigBoard";
import TeamDrawer from "../components/league/TeamDrawer";
import TeamFilterSheet from "../components/scouting/TeamFilterSheet";
import { deriveBounds, applyRanges, activeFilters } from "../utils/scoutingFilters";

// conf_odds is stored as a raw American moneyline. Negatives already carry their sign;
// positives need one added back for display.
const formatAmericanOdds = (odds) => (odds > 0 ? `+${odds}` : `${odds}`);

// The helmet is the only frozen column, at 40px.
//
// Something has to stay put or there is no telling which row you are reading
// once the table scrolls, and the logo does that in the least space: it is
// distinctive enough to identify a team at a glance, where the name needs
// 150px or more to avoid truncating "Jacksonville State".
//
// Freezing star, helmet and name together came to 290px, which on a 390px
// phone left 100px to scroll through and put the stats the page exists for out
// of reach. 40px leaves 350px.
//
// minWidth and maxWidth are locked to the same value on purpose. The table is
// auto-layout, where a declared width is only a suggestion, and a frozen column
// laying out wider or narrower than declared either overlaps its neighbour or
// leaves a gap with scrolling content visible through it.
const fixedWidth = (width) => ({
  width: `${width}px`,
  minWidth: `${width}px`,
  maxWidth: `${width}px`,
});

const PIN_STAR = fixedWidth(40);
const PIN_NAME = fixedWidth(210);
const PIN_LOGO = {
  ...fixedWidth(40),
  position: "sticky",
  left: 0,
};

// One row colour, used by the <tr> and by both pinned columns.
//
// The pinned cells cannot inherit it. A sticky cell is lifted out of the row's
// paint, so `backgroundColor: "inherit"` left them effectively transparent and
// the school name scrolled visibly under the helmet. They need the same opaque
// colour set on them directly, which is why this is a function rather than a
// value on the row.
//
// Drafted rows are muted with colour rather than opacity for the same reason:
// opacity applies to the whole row including its background, so no background
// on a sticky cell inside it can ever be fully opaque.
const rowBackground = (team, index, hovered) => {
  if (team.isDrafted) return "#FEF2F2";
  if (hovered) return "#EFF6FF";
  return index % 2 === 0 ? "#FFFFFF" : "#F9FAFB";
};

function Scouting() {
  const { leagueId } = useParams();
  const { isDraftComplete } = useLeague();
  const { teams: authTeams, teamsError } = useAuth();
  const { pickedTeamIds } = useDraftContext();
  const { preseasonData, error: preseasonError } = usePreseasonStats();
  const { count, limit, isOnBoard, rankOf, toggle } = useBigBoard(leagueId, pickedTeamIds);

  // Ascending on American odds = best price (biggest favorite) first.
  const [sortConfig, setSortConfig] = useState({ key: "confOdds", direction: "asc" });
  const [conferenceFilter, setConferenceFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [drawerTeam, setDrawerTeam] = useState(null);
  const [boardNotice, setBoardNotice] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  // Hover lives in state rather than in an onMouseEnter that writes
  // style.backgroundColor: the two pinned columns need the same colour as the
  // row they belong to, and a DOM write on the <tr> never reaches them.
  const [hoveredRow, setHoveredRow] = useState(null);
  // Only the ranges the user has moved. A key absent here means "full range",
  // which keeps "is this filter active" a comparison against the bounds rather
  // than a separate flag to keep in sync.
  const [ranges, setRanges] = useState({});

  useEffect(() => {
    if (!boardNotice) return;
    const id = setTimeout(() => setBoardNotice(""), 3000);
    return () => clearTimeout(id);
  }, [boardNotice]);

  const teams = useMemo(() =>
    Object.values(authTeams)
      .filter((t) => (t.classification || "").toLowerCase() === "fbs")
      .map((t) => {
        const pre = preseasonData[t.id] || {};
        return {
          ...t,
          isDrafted:           pickedTeamIds.has(t.id),
          boardRank:           rankOf(t.id),
          // Preseason SOS, not the in-season one on team_season_stats — this
          // page is pre-draft, and the two diverge once games are played.
          sos_rank:            pre.sos_rank ?? null,
          prev_year_record:    pre.prev_year_record ?? null,
          prev_year_ats:       pre.prev_year_ats ?? null,
          confOdds:            pre.conf_odds ?? null,
          predictedWins:       pre.predicted_wins ?? null,
          powerRank:           pre.power_rank ?? null,
          retStarters:         pre.ret_starters ?? null,
        };
      }),
  [authTeams, pickedTeamIds, preseasonData, rankOf]);

  const allTeams = useMemo(() =>
    Object.fromEntries(teams.map((t) => [t.school, t])),
  [teams]);

  // Derived from every FBS team, not from the filtered list, so the slider
  // ends stay put instead of collapsing as the user narrows things down.
  // Must sit above the isDraftComplete return to keep the hook order stable.
  const bounds = useMemo(() => deriveBounds(teams), [teams]);

  if (isDraftComplete) return <Navigate to="/home" />;

  const handleToggleBoard = (team) => {
    const result = toggle(team.id);
    if (!result.ok && result.reason === "full") {
      setBoardNotice(`Big board is full (${limit} teams). Remove one before adding ${team.school}.`);
    } else {
      setBoardNotice("");
    }
  };

  const sortBy = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const setRange = (key, next) => setRanges((prev) => ({ ...prev, [key]: next }));
  const resetRanges = () => setRanges({});
  const active = activeFilters(ranges, bounds);

  const searchedTeams = teams.filter((team) => {
    const matchesSearch = team.school.toLowerCase().includes(searchQuery.toLowerCase());
    const p4 = ["SEC", "ACC", "Big Ten", "Big 12"];
    if (conferenceFilter === "All") return matchesSearch;
    if (conferenceFilter === "P4") return matchesSearch && p4.includes(team.conference);
    if (conferenceFilter === "Available") return matchesSearch && !team.isDrafted;
    if (conferenceFilter === "Drafted") return matchesSearch && team.isDrafted;
    if (conferenceFilter === "Board") return matchesSearch && team.boardRank != null;
    return matchesSearch && team.conference === conferenceFilter;
  });

  const filteredTeams = applyRanges(searchedTeams, ranges, bounds);

  const sortedTeams = [...filteredTeams].sort((a, b) => {
    const aValue = sortValue(a[sortConfig.key], sortConfig.key);
    const bValue = sortValue(b[sortConfig.key], sortConfig.key);

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    if (sortConfig.key === "school") {
      return sortConfig.direction === "asc"
        ? a.school.localeCompare(b.school)
        : b.school.localeCompare(a.school);
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    return sortConfig.direction === "asc"
      ? aValue.toString().localeCompare(bValue.toString())
      : bValue.toString().localeCompare(aValue.toString());
  });

  const uniqueConferences = [...new Set(teams.map((team) => team.conference))].sort();
  const availableCount = teams.filter((t) => !t.isDrafted).length;
  const draftedCount   = teams.filter((t) => t.isDrafted).length;

  const TeamLogo = ({ teamName, size = 24, isDrafted = false }) => {
    const team = allTeams[teamName];
    return (
      <div style={{ opacity: isDrafted ? 0.4 : 1, filter: isDrafted ? "grayscale(100%)" : "none", flexShrink: 0 }}>
        <TeamLogoImage teamName={teamName} primaryColor={team?.colors?.primary || team?.color} size={size} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">


      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-24">

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-2xl">🔍</span>
            <h1 className="text-2xl font-black text-gray-900">Team Scouting</h1>
          </div>
          <p className="text-sm text-gray-500 pl-10">
            {filteredTeams.length} teams · {availableCount} available, {draftedCount} drafted · big board {count}/{limit}
          </p>
        </div>

        {/* The table still renders without preseason stats, as a column of
            dashes per team, which reads as a broken page rather than a failed
            fetch. Say which it is. */}
        {preseasonError && teams.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700 text-sm mb-4 flex items-center justify-between gap-3">
            <span>Preseason stats did not load, so the ratings columns are empty.</span>
            <button
              onClick={() => window.location.reload()}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
            >
              Retry
            </button>
          </div>
        )}

        {boardNotice && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700 text-sm mb-4">
            {boardNotice}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm mb-5">
          <div className="flex flex-wrap gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Search Teams
              </label>
              <input
                type="text"
                placeholder="Search by school name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="px-4 py-2.5 text-gray-900 bg-white border border-gray-200 rounded-xl w-64 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 placeholder:text-gray-400 text-sm"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Conference
              </label>
              <select
                value={conferenceFilter}
                onChange={(e) => setConferenceFilter(e.target.value)}
                className="px-4 py-2.5 text-gray-900 bg-white border border-gray-200 rounded-xl cursor-pointer outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm"
              >
                <option value="All">All Conferences</option>
                <option value="P4">Power 4 Only</option>
                <option value="Available">Available Only</option>
                <option value="Drafted">Drafted Only</option>
                <option value="Board">My Big Board</option>
                {uniqueConferences.map((conf) => (
                  <option key={conf} value={conf}>{conf}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5 flex-1 min-w-[140px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Stats
              </label>
              <button
                onClick={() => setFilterOpen(true)}
                className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold cursor-pointer transition-colors hover:bg-blue-700"
              >
                <SlidersHorizontal size={16} />
                Filter
                {active.length > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold">
                    {active.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {active.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
              {active.map((filter) => {
                const [lo, hi] = ranges[filter.key];
                return (
                  <button
                    key={filter.key}
                    onClick={() => setRange(filter.key, [bounds[filter.key].min, bounds[filter.key].max])}
                    title={`Clear ${filter.label}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold cursor-pointer hover:bg-blue-100 transition-colors"
                  >
                    {filter.label} {filter.format(lo)}–{filter.format(hi)}
                    <span className="text-blue-400 font-bold">×</span>
                  </button>
                );
              })}
              <button
                onClick={resetRanges}
                className="px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 bg-gray-50">
            <h3 className="text-base font-bold text-gray-900 mb-0.5">FBS Team Database</h3>
            <p className="text-xs text-gray-500">
              Click any column header to sort · Click team name to view details · Greyed out teams are drafted ·
              Star up to {limit} teams to build your big board
            </p>
          </div>

          {/* The header sticks to the top of this box, not to the viewport.
              An ancestor with a non-visible overflow becomes the containing
              block for sticky, and this element already needs overflow-x for
              the pinned first columns, so the vertical scroll has to happen
              here too. Without the height cap the box never scrolls and the
              sticky header has nothing to stick against. */}
          <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
            <table className="w-full text-sm min-w-[1180px]" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr style={{ backgroundColor: "#0072BC", color: "white" }}>
                  <th style={{
                    ...thStyle,
                    textAlign: "center",
                    padding: "11px 4px",
                    ...PIN_STAR,
                    top: 0,
                    backgroundColor: HEADER_BLUE,
                    borderRight: "none"
                  }}>
                    ★
                  </th>

                  <th style={{
                    ...thStyle,
                    textAlign: "center",
                    ...PIN_LOGO,
                    top: 0,
                    // Above the other header cells: pinned on both axes, so it
                    // has to cover them as the table scrolls left.
                    zIndex: 30,
                    backgroundColor: HEADER_BLUE,
                    borderRight: "1px solid rgba(255,255,255,0.25)"
                  }}>
                  </th>

                  <th
                    onClick={() => sortBy("school")}
                    style={{
                      ...thStyle,
                      textAlign: "left",
                      // thStyle already pins it to the top and makes it a
                      // containing block for the absolutely positioned label
                      // below, which is what the sticky here was for.
                      ...PIN_NAME,
                      top: 0,
                      paddingLeft: "0",
                      borderLeft: "none",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = HEADER_BLUE_HOVER}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = HEADER_BLUE}
                  >
                    <span style={{
                      position: "absolute",
                      left: "20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      School
                      {sortConfig.key === "school" && (
                        <span style={{ fontSize: "10px" }}>
                          {sortConfig.direction === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </span>
                  </th>

                  <Th label="Board"        sortKey="boardRank"           sortBy={sortBy} sortConfig={sortConfig} />
                  <Th label="Conf Odds"    sortKey="confOdds"            sortBy={sortBy} sortConfig={sortConfig} />
                  <Th label="Power Rank"   sortKey="powerRank"           sortBy={sortBy} sortConfig={sortConfig} />
                  <Th label="SOS Rank"     sortKey="sos_rank"            sortBy={sortBy} sortConfig={sortConfig} />
                  <Th label="Ret. Starters (%)" sortKey="retStarters"    sortBy={sortBy} sortConfig={sortConfig} />
                  <Th label="Pred. Wins"   sortKey="predictedWins"       sortBy={sortBy} sortConfig={sortConfig} />
                  <Th label="2025 Record"  sortKey="prev_year_record"    sortBy={sortBy} sortConfig={sortConfig} />
                  <Th label="2025 vs Margin" sortKey="prev_year_ats"       sortBy={sortBy} sortConfig={sortConfig} />
                </tr>
              </thead>
              <tbody>
                {sortedTeams.map((team, i) => (
                  <tr
                    key={i}
                    style={{
                      backgroundColor: rowBackground(team, i, hoveredRow === i),
                      color: team.isDrafted ? '#9CA3AF' : '#111827',
                      cursor: 'default',
                    }}
                    onMouseEnter={() => setHoveredRow(i)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td style={{
                      ...tdStyle,
                      textAlign: "center",
                      padding: "11px 4px",
                      ...PIN_STAR,
                    }}>
                      <BoardStar
                        team={team}
                        starred={isOnBoard(team.id)}
                        onToggle={handleToggleBoard}
                      />
                    </td>

                    <td style={{
                      ...tdStyle,
                      textAlign: "center",
                      paddingRight: "4px",
                      ...PIN_LOGO,
                      zIndex: 1,
                      backgroundColor: rowBackground(team, i, hoveredRow === i),
                      borderRight: "1px solid #E5E7EB",
                    }}>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <TeamLogo teamName={team.school} size={24} isDrafted={team.isDrafted} />
                      </div>
                    </td>

                    <td style={{
                      ...tdStyle,
                      textAlign: "left",
                      paddingLeft: "8px",
                      ...PIN_NAME,
                    }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                          <div
                            onClick={() => setDrawerTeam(team.school)}
                            style={{
                              cursor: "pointer",
                              color: team.isDrafted ? "#9CA3AF" : "#0072BC",
                              fontWeight: "600",
                              fontSize: "14px"
                            }}
                            onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                            onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                          >
                            {team.school}
                          </div>
                          {team.isDrafted && (
                            <span style={{
                              backgroundColor: "#FEE2E2",
                              color: "#DC2626",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "10px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                            }}>
                              DRAFTED
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "11px", color: "#9CA3AF", fontWeight: "500" }}>
                          {team.conference}
                        </div>
                      </div>
                    </td>

                    <td style={tdStyle}>
                      {team.boardRank != null ? (
                        <span style={{
                          backgroundColor: "#DBEAFE",
                          color: "#1D4ED8",
                          padding: "2px 7px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "700",
                        }}>
                          #{team.boardRank}
                        </span>
                      ) : "-"}
                    </td>

                    <td style={tdStyle}>
                      {team.confOdds != null ? (
                        <span style={{
                          backgroundColor: team.confOdds <= 500 ? "#DCFCE7" : team.confOdds <= 2500 ? "#FEF3C7" : "#FEE2E2",
                          color: team.confOdds <= 500 ? "#166534" : team.confOdds <= 2500 ? "#92400E" : "#991B1B",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}>
                          {formatAmericanOdds(team.confOdds)}
                        </span>
                      ) : "-"}
                    </td>

                    <td style={tdStyle}>{team.powerRank ?? "-"}</td>

                    <td style={tdStyle}>{team.sos_rank ?? "-"}</td>

                    <td style={tdStyle}>{team.retStarters ?? "-"}</td>

                    <td style={tdStyle}>
                      {team.predictedWins != null
                        ? <span style={{ fontWeight: "600", color: "#111827" }}>{team.predictedWins}</span>
                        : "-"}
                    </td>

                    <td style={tdStyle}>{team.prev_year_record ?? "-"}</td>

                    <td style={tdStyle}>{team.prev_year_ats ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* An empty table has two very different causes, and telling a
              manager their filters matched nothing when the team data never
              arrived sent them looking in the wrong place. */}
          {sortedTeams.length === 0 && (
            <div className="p-10 text-center">
              {teamsError || teams.length === 0 ? (
                <>
                  <p className="text-gray-900 text-sm font-semibold mb-1">Team data did not load.</p>
                  <p className="text-gray-500 text-sm mb-4">Check your connection and try again.</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                  >
                    Reload
                  </button>
                </>
              ) : (
                <p className="text-gray-500 text-sm">No teams found matching your criteria.</p>
              )}
            </div>
          )}
        </div>
      </div>
      <TeamDrawer teamName={drawerTeam} onClose={() => setDrawerTeam(null)} onSelectTeam={setDrawerTeam} />
      <TeamFilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        bounds={bounds}
        ranges={ranges}
        onChangeRange={setRange}
        onReset={resetRanges}
        resultCount={filteredTeams.length}
      />
    </div>
  );
}

// "9-3" / "9-3-1" sort by win pct, not alphabetically ("10-3" < "9-3" as text).
const RECORD_KEYS = new Set(["prev_year_record", "prev_year_ats"]);

const sortValue = (value, key) => {
  if (!RECORD_KEYS.has(key) || typeof value !== "string") return value;
  const parts = value.split("-").map(Number);
  if (parts.length < 2 || parts.some(Number.isNaN)) return value;
  const [wins, losses, ties = 0] = parts;
  const games = wins + losses + ties;
  return games > 0 ? (wins + ties * 0.5) / games : null;
};

// A drafted team can't be starred — the 018 trigger strips it from every board
// in the league the moment the pick lands.
const BoardStar = ({ team, starred, onToggle }) => (
  <button
    onClick={() => onToggle(team)}
    disabled={team.isDrafted}
    title={
      team.isDrafted ? "Already drafted"
        : starred ? "Remove from big board"
        : "Add to big board"
    }
    style={{
      background: "none",
      border: "none",
      padding: "2px",
      cursor: team.isDrafted ? "not-allowed" : "pointer",
      opacity: team.isDrafted ? 0.25 : 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto",
    }}
  >
    <Star
      size={16}
      color={starred ? "#2563EB" : "#D1D5DB"}
      fill={starred ? "#2563EB" : "none"}
    />
  </button>
);

const Th = ({ label, sortKey, sortBy, sortConfig }) => (
  <th
    onClick={() => sortBy(sortKey)}
    style={{ ...thStyle, cursor: "pointer" }}
    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = HEADER_BLUE_HOVER}
    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = HEADER_BLUE}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <span>{label}</span>
      {sortConfig.key === sortKey && (
        <span style={{ fontSize: "10px" }}>
          {sortConfig.direction === "asc" ? "▲" : "▼"}
        </span>
      )}
    </div>
  </th>
);

// The header row sticks to the top of the scrolling table body. Each cell
// carries its own opaque background: a sticky cell leaves the <tr> behind, so
// a transparent one would let the rows show through as they pass under it.
const HEADER_BLUE = "#0072BC";
const HEADER_BLUE_HOVER = "#1A82C9";

const thStyle = {
  borderRight: "1px solid rgba(255,255,255,0.2)",
  padding: "11px 16px",
  textAlign: "left",
  fontWeight: "700",
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: HEADER_BLUE,
};

// No colour here on purpose: the <tr> sets it, so a drafted row can grey its
// text without opacity, which sticky cells cannot survive.
const tdStyle = {
  padding: "11px 16px",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "middle",
  textAlign: "center",
};

export default Scouting;
